/**
 * User Out-of-Office Resource Hooks
 *
 * Type-safe hooks for out-of-office API operations.
 * Built on top of the generic API hooks with OOO-specific defaults.
 */

import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

import { queryKeys } from '../../queryKeys';
import { useApiCreate, useApiDelete, useApiUpdate } from '../useApiMutation';
import { useApiList } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type OutOfOfficeListResponse =
  paths['/api/v1/users/me/out-of-office']['get']['responses']['200']['content']['application/json'];
type OutOfOfficeCreateBody =
  paths['/api/v1/users/me/out-of-office']['post']['requestBody']['content']['application/json'];
type OutOfOfficeUpdateBody = NonNullable<
  paths['/api/v1/users/me/out-of-office/{id}']['patch']['requestBody']
>['content']['application/json'];

type OutOfOfficeRecord = NonNullable<
  paths['/api/v1/users/me/out-of-office']['get']['responses']['200']['content']['application/json']['data']
>[number];

// =============================================================================
// Query Hooks
// =============================================================================

interface UseMyOutOfOfficeQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch the authenticated user's out-of-office periods
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useMyOutOfOfficeQuery();
 * // data?.data is an array of OutOfOfficeRecord
 * ```
 */
export function useMyOutOfOfficeQuery(options?: UseMyOutOfOfficeQueryOptions) {
  return useApiList('/api/v1/users/me/out-of-office', {
    enabled: options?.enabled,
    queryKey: queryKeys[ResourceType.OUT_OF_OFFICE].list(),
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseCreateOutOfOfficeOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Create a new out-of-office period
 *
 * @example
 * ```tsx
 * const create = useCreateOutOfOffice({
 *   onSuccess: () => toast.success('Out-of-office period created'),
 * });
 *
 * create.mutate({ start_date: '2024-12-20', end_date: '2025-01-05', reason: 'Holiday' });
 * ```
 */
export function useCreateOutOfOffice(options?: UseCreateOutOfOfficeOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users/me/out-of-office',
  });
}

interface UseCancelOutOfOfficeOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Cancel an out-of-office period
 *
 * @example
 * ```tsx
 * const cancel = useCancelOutOfOffice({
 *   onSuccess: () => toast.success('Out-of-office period cancelled'),
 * });
 *
 * cancel.mutate({ id: oooId });
 * ```
 */
export function useCancelOutOfOffice(options?: UseCancelOutOfOfficeOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users/me/out-of-office/{id}',
  });
}

interface UseUpdateOutOfOfficeOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export function useUpdateOutOfOffice(options?: UseUpdateOutOfOfficeOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users/me/out-of-office/{id}',
  });
}

// =============================================================================
// Exports
// =============================================================================

export type { OutOfOfficeCreateBody, OutOfOfficeListResponse, OutOfOfficeRecord, OutOfOfficeUpdateBody };
